import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription, map, debounceTime, distinctUntilChanged } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
	FieldExpression,
	LuceneExpression,
	NodeExpression,
	term,
	parse as luceneToParse,
	toString as luceneToString
} from 'lucene';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { UNDERSCORE } from '../../../shared/components/utils/constants';
import { OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { ORDER, ORDER_SORT } from '../../../shared/models/settings';
import { FavoriteOrders } from '../../models/favorite-orders';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { ListFilter } from '../../../shared/models/filter';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { ScheduleBusinessOrderService } from '../../services/schedule-business-order.service';
import { ScheduleBusinessOrder } from '../../models/schedule-business-order';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

interface OrderExtended extends ScheduleBusinessOrder {
	isFavoriteDisabled?: boolean;
}

@Component({
	selector: 'copo-schedule-business-order-list',
	templateUrl: './schedule-business-order-list.component.html',
	styleUrl: './schedule-business-order-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		VirtualScrollerDirective,
		NgFor,
		RouterLink,
		LoadingSpinnerComponent,
		NumberPipe,
		DimensionPipe
	]
})
export class ScheduleBusinessOrderListComponent implements OnInit, OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;

	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	sortWay = OrderByDirection.DESC;
	sortField?: 'order';
	isLoading = true;
	favoriteSBOrdersSubscription?: Subscription;
	saveFavoriteSBOrdersSubscription?: Subscription;
	deletefavoriteSBOrdersSubscription?: Subscription;
	favoriteSBOrders: FavoriteOrders[] = [];
	requiredFields: (keyof ScheduleBusinessOrder)[] = [
		'steelgradeDescription',
		'soldToDescription',
		'productCategoryDescription',
		'millDescription',
		'customerArticleReference',
		'supplierOrderNumber',
		'customerOrderNumber',
		'thickness',
		'length',
		'width',
		'realizedCumul'
	];

	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription?: Subscription;
	filters = '';
	mergedFilter?: LuceneExpression;
	currentFilters?: ListFilter;
	datacenterType: DatacenterType;
	constructor(
		private readonly scheduleBusinessOrderService: ScheduleBusinessOrderService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService,
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.currentFilters = this.getAppliedFilters();
		let criterionFilterNode;
		if (this.listFilterService
			.getLuceneFromAllFilters(this.currentFilters)) {
			criterionFilterNode = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		}

		if (criterionFilterNode !== undefined) {
			this.mergedFilter = criterionFilterNode;
			this.filters = luceneToString(criterionFilterNode);
		}

		this.user = this.userService.user;

		const orderTrackingSortList = this.user.getSettings('ordertrackingsortlist');
		if (!orderTrackingSortList) {
			return;
		}
		if (orderTrackingSortList.value.ordertrackingsortlist === 'order_desc') {
			this.sortWay = OrderByDirection.DESC;
			this.sortField = ORDER;
		} else {
			this.sortWay = OrderByDirection.ASC;
			this.sortField = ORDER;
		}
	}

	get dataId() {
		return `order-tracking-schedule-business-order-${
			this.sortWay
		}-${
			ORDER_SORT
		}-${
			this.filters
		}`;
	}

	ngOnInit(): void {
		this.favoriteSBOrdersSubscription = this.scheduleBusinessOrderService
			.getFavoriteScheduleBusinessOrders().subscribe({
				next: favoriteSBOrders => {
					this.favoriteSBOrders = favoriteSBOrders;
					this.isLoading = false;
				},
				error: (error: unknown) => {
					console.error(error);
					this.isLoading = false;
				},
			});

		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.filters = value !== ''
				? this.mergedFilter
					? this.createQuickSearchAndFilterQuery(this.createQuickSearchFilter(value), this.mergedFilter)
					: this.createQuickSearchFilter(value)
				: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
		});
	}

	ngOnDestroy() {
		if (this.favoriteSBOrdersSubscription) {
			this.favoriteSBOrdersSubscription.unsubscribe();
		}

		if (this.saveFavoriteSBOrdersSubscription) {
			this.saveFavoriteSBOrdersSubscription.unsubscribe();
		}

		if (this.deletefavoriteSBOrdersSubscription) {
			this.deletefavoriteSBOrdersSubscription.unsubscribe();
		}

		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<ScheduleBusinessOrder[]>) => void) {
		provideData(
			this.scheduleBusinessOrderService.getScheduleBusinessOrders(
				this.requiredFields,
				this.filters,
				index,
				limit,
				new OrderBy(ORDER_SORT, this.sortWay),
				false
			).pipe(map(res => res.results.map((scheduleBusinessOrder: OrderExtended) => {
				const comparedFavoriteNumber = this.favoriteSBOrders.find(
					favoriteSBOrders =>
						favoriteSBOrders.supplierOrderReference === scheduleBusinessOrder.supplierOrderNumber
				);
				scheduleBusinessOrder.favorite = comparedFavoriteNumber;
				scheduleBusinessOrder.isFavorite = Boolean(comparedFavoriteNumber);
				scheduleBusinessOrder.isFavoriteDisabled = false;

				return scheduleBusinessOrder;
			})))
		);
	}

	deleteFavoriteSBOrder(scheduleBusinessOrder: OrderExtended) {
		if (!scheduleBusinessOrder.favorite?.id) {
			return;
		}

		scheduleBusinessOrder.isFavoriteDisabled = true;

		this.deletefavoriteSBOrdersSubscription = this.scheduleBusinessOrderService
			.deleteScheduleBusinessOrders(scheduleBusinessOrder.favorite.id)
			.subscribe({
				next: () => {
					scheduleBusinessOrder.isFavorite = false;
					scheduleBusinessOrder.isFavoriteDisabled = false;
				},
				error: (error: unknown) => {
					console.error(error);
					scheduleBusinessOrder.isFavoriteDisabled = false;
				}
			});
	}

	saveFavoriteSBOrder(scheduleBusinessOrder: OrderExtended) {
		scheduleBusinessOrder.isFavoriteDisabled = true;

		const obj: FavoriteOrders = {
			supplierOrderReference: scheduleBusinessOrder.supplierOrderNumber,
			supplierOrderItem: ''
		};

		this.saveFavoriteSBOrdersSubscription = this.scheduleBusinessOrderService.saveScheduleBusinessOrders(obj)
			.subscribe({
				next: res => {
					scheduleBusinessOrder.favorite = {
						id: res.id,
						supplierOrderReference: res.supplierOrderReference,
						supplierOrderItem: res.supplierOrderItem
					};

					scheduleBusinessOrder.isFavorite = true;
					scheduleBusinessOrder.isFavoriteDisabled = false;
				},
				error: (error: unknown) => {
					console.error(error);
					scheduleBusinessOrder.isFavoriteDisabled = false;
				}
			});
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const supplierOrderReferenceField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const customerOrderReferenceField: FieldExpression = {
			field: 'customerOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const customerArticleReferenceField: FieldExpression = {
			field: 'customerArticleReference',
			term: escapedTextFilter,
			quoted: false
		};

		const steelgradeDescriptionField: FieldExpression = {
			field: 'steelgradeDescription',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierCustomerNode = {
			left: supplierOrderReferenceField,
			operator: 'OR',
			right: customerOrderReferenceField,
			parenthesized: false
		};

		const articleSteelgradeNode = {
			left: customerArticleReferenceField,
			operator: 'OR',
			right: steelgradeDescriptionField,
			parenthesized: false
		};

		const globalNode = {
			left: supplierCustomerNode,
			operator: 'OR',
			right: articleSteelgradeNode,
			parenthesized: true
		};

		return luceneToString(globalNode);
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: this.currentFilters.datacenterType
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: this.datacenterType }, relativeTo: this.route }
		);
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: [],
			datacenterType: this.datacenterType
		};
		const appliedFilter = this.listFilterService.getAppliedFilters(this.datacenterType)
			?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
